import React, { useRef } from "react";
import ReactToPrint from "react-to-print";
import { useSelector } from "react-redux";
import PrintIcon from "@mui/icons-material/Print";
import { Box, IconButton, Typography, Grid } from "@mui/material";

import { getFullName } from "../../../../helpers/formatters";
import usePrintStyles from "../../../../hooks/usePrintStyles";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { convertADtoBS } from "../../../../components/Calendar/functions/calendarFunctions";
import LabPrintLetterHead from "../../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import LabPrintLetterHeadCentered from "../../../Lab/LabPrint/LabprintComponents/LabPrintLetterheadCentered";
import {
  TEETH_NUMBERS,
  DENTAL_ITEMS,
  PONTIC_DESIGNS,
  OCCLUSAL_STAIN_OPTIONS
} from "../../../../const/DentalLabOrder";
import { DentalLabOrder } from "../../../../interfaces/DentalLabOrder";
import { RootState } from "../../../../store";

// @ts-expect-error - image
import Logo from "../../../../../assets/images/poweredByOkhati.svg";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";
import { spFullNameSelector } from "../../../../reducers/serviceProvider";

interface DentalLabOrderPrintProps {
  dentalLabOrder: DentalLabOrder;
}

// eslint-disable-next-line import/prefer-default-export
export function DentalLabOrderPrint({ dentalLabOrder }: DentalLabOrderPrintProps): React.ReactNode {
  const printRef = useRef<HTMLDivElement>(null);
  const serviceProviders = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  const serviceProvider = serviceProviders.find(
    (sp: ServiceProvider) => sp.id === Number(dentalLabOrder.serviceProviderId)
  );

  const rc = useCurrentResourceCentre();
  usePrintStyles({ pageStyle: "margin: 5mm 10mm" }).runPrintStyles();

  const centralizedHeader: boolean = rc?.settings?.printSettings.centralizedHeader;
  const clinicNameFontScale = rc?.settings?.printSettings.clinicNameFontScale;
  const panNo = rc?.settings?.billingSettings?.panNo;

  let letterHeadComponent = <Box />;
  if (centralizedHeader) {
    letterHeadComponent = (
      <LabPrintLetterHeadCentered
        resourceCentre={{
          ...rc,
          panNo,
          resourceCentreLogo: rc?.resourceCentreLogo ?? ""
        }}
        titleFontScale={clinicNameFontScale}
      />
    );
  } else {
    letterHeadComponent = (
      <LabPrintLetterHead
        resourceCentre={{
          ...rc,
          panNo,
          resourceCentreLogo: rc?.resourceCentreLogo ?? ""
        }}
        titleFontScale={clinicNameFontScale}
      />
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <ReactToPrint
        trigger={() => (
          <IconButton
            disableRipple
            size="small"
            sx={{
              color: "black",
              "&.MuiButtonBase-root:hover": {
                bgcolor: "transparent"
              }
            }}
          >
            <PrintIcon />
          </IconButton>
        )}
        content={() => printRef.current}
      />

      <iframe className="displayContents" title="printContainer">
        <div ref={printRef}>
          <Box component="table" width="100%">
            <thead>
              <tr>
                <td>
                  <Box sx={{ my: 2 }}>{letterHeadComponent}</Box>
                </td>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {/* Title */}
                    <Box
                      sx={{
                        textAlign: "center",
                        borderBottom: "1px solid #e0e0e0",
                        pb: 1
                      }}
                    >
                      <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                        DENTAL LAB ORDER
                      </Typography>
                    </Box>

                    {/* Lab Order Info */}
                    <Box sx={{ display: "flex" }}>
                      <Box sx={{ flex: "1 1 0" }}>
                        <Typography>
                          <strong>Patient: </strong> {getFullName(dentalLabOrder.client)}
                        </Typography>

                        <Typography>
                          <strong>Issued By: </strong>{" "}
                          {serviceProvider ? spFullNameSelector(serviceProvider) : "-"}
                        </Typography>

                        <Typography>
                          <strong>Issued Date: </strong>
                          {convertADtoBS(new Date(dentalLabOrder.orderDate)).formatted}
                        </Typography>
                      </Box>

                      <Box sx={{ flex: "1 1 0" }}>
                        <Typography>
                          <strong>Delivery Date:</strong>{" "}
                          {dentalLabOrder.deliveryDate
                            ? convertADtoBS(new Date(dentalLabOrder.deliveryDate)).formatted
                            : "-"}
                          {/* trial dates */}
                          {dentalLabOrder.trialDates.length > 0 && (
                            <Typography>
                              <strong>Trial Dates:</strong>{" "}
                              {dentalLabOrder.trialDates
                                .map((d) => convertADtoBS(new Date(d)).formatted)
                                .join(", ")}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Tooth Number Grid */}
                    <Box sx={{ border: "1px solid #e0e0e0" }}>
                      <Typography
                        sx={{
                          p: 0.5,
                          fontWeight: 600,
                          bgcolor: "#e0e0e0"
                        }}
                      >
                        Tooth Number
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          p: 1
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          {TEETH_NUMBERS.TOP.map((tooth: string) => (
                            <Box
                              key={tooth}
                              sx={{
                                width: "25px",
                                height: "25px",
                                border: "1px solid #b2beb5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "8px",
                                bgcolor: dentalLabOrder.teeth?.includes(tooth)
                                  ? "#b2beb5"
                                  : "white",
                                color: dentalLabOrder.teeth?.includes(tooth) ? "white" : "black",
                                fontWeight: dentalLabOrder.teeth?.includes(tooth) ? 600 : 400
                              }}
                            >
                              {tooth}
                            </Box>
                          ))}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          {TEETH_NUMBERS.BOTTOM.map((tooth) => (
                            <Box
                              key={tooth}
                              sx={{
                                width: "25px",
                                height: "25px",
                                border: "1px solid #b2beb5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "8px",
                                bgcolor: dentalLabOrder.teeth?.includes(tooth) ? "#000" : "white",
                                color: dentalLabOrder.teeth?.includes(tooth) ? "white" : "black",
                                fontWeight: dentalLabOrder.teeth?.includes(tooth) ? 600 : 400
                              }}
                            >
                              {tooth}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    {/* Items/Restoration Types */}
                    <Box sx={{ border: "1px solid #e0e0e0" }}>
                      <Typography
                        sx={{
                          p: 0.5,
                          fontWeight: 600,
                          bgcolor: "#e0e0e0"
                        }}
                      >
                        Items/Restoration Types
                      </Typography>
                      <Grid container sx={{ p: 1 }}>
                        {DENTAL_ITEMS.map((item) => (
                          <Grid size={3} key={item}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                sx={{
                                  width: "12px",
                                  height: "12px",
                                  border: "1px solid #b2beb5",
                                  mr: 1,
                                  bgcolor: dentalLabOrder.items?.includes(item)
                                    ? "#b2beb5"
                                    : "white"
                                }}
                              />
                              <Typography>{item}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Shade Details and Pontic Designs */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid size={6}>
                        <Box sx={{ border: "1px solid #e0e0e0" }}>
                          <Typography
                            sx={{
                              p: 0.5,
                              fontWeight: 600,
                              bgcolor: "#e0e0e0"
                            }}
                          >
                            Shade Details
                          </Typography>
                          <Box
                            sx={{
                              p: 1,
                              minHeight: "40px",
                              fontSize: "10px",
                              fontWeight: 600
                            }}
                          >
                            {dentalLabOrder.shadeDetails?.join(", ") || ""}
                          </Box>
                        </Box>
                      </Grid>

                      <Grid size={6}>
                        <Box sx={{ border: "1px solid #e0e0e0" }}>
                          <Typography
                            sx={{
                              p: 0.5,
                              fontWeight: 600,
                              bgcolor: "#e0e0e0"
                            }}
                          >
                            Occlusal Stains
                          </Typography>
                          <Grid container sx={{ p: 1 }} spacing={0.2}>
                            {OCCLUSAL_STAIN_OPTIONS.map((stain) => (
                              <Grid size={6} key={stain}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Box
                                    sx={{
                                      width: "12px",
                                      height: "12px",
                                      border: "1px solid #b2beb5",
                                      mr: 1,
                                      bgcolor: dentalLabOrder.occlusalStains?.includes(stain)
                                        ? "#b2beb5"
                                        : "white"
                                    }}
                                  />
                                  <Typography sx={{ textTransform: "capitalize" }}>
                                    {stain}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Pontic Designs */}
                    <Box sx={{ border: "1px solid #e0e0e0" }}>
                      <Typography
                        sx={{
                          p: 0.5,
                          fontWeight: 600,
                          bgcolor: "#e0e0e0"
                        }}
                      >
                        Pontic Design
                      </Typography>
                      <Grid container spacing={0.2} sx={{ p: 1 }}>
                        {PONTIC_DESIGNS.map((design) => (
                          <Grid size={4} key={design.value}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                sx={{
                                  width: "12px",
                                  height: "12px",
                                  border: "1px solid #e0e0e0",
                                  mr: 1,
                                  bgcolor: dentalLabOrder.ponticDesigns?.includes(design.value)
                                    ? "#b2beb5"
                                    : "white"
                                }}
                              />
                              <Typography>{design.value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Special Instructions */}
                    <Box sx={{ border: "1px solid #e0e0e0" }}>
                      <Typography
                        sx={{
                          p: 0.5,
                          fontWeight: 600,
                          bgcolor: "#e0e0e0"
                        }}
                      >
                        Special Instructions
                      </Typography>
                      <Box
                        sx={{
                          minHeight: "30px",
                          p: 1,
                          whiteSpace: "pre-wrap"
                        }}
                      >
                        {dentalLabOrder.note || ""}
                      </Box>
                    </Box>

                    {/* signature */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pt: 3
                      }}
                    >
                      <Typography>
                        Signature:{" "}
                        <Box component="span" sx={{ color: "#e0e0e0" }}>
                          ________________________________
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                </td>
              </tr>
            </tbody>
          </Box>

          {/* Footer */}
          <div className="billFooterPos" style={{ width: "100%" }}>
            <Box
              sx={{
                width: "100%",
                borderTop: "1px solid #ccc",
                pt: 1,
                display: "flex",
                justifyContent: "flex-end",
                px: 2
              }}
            >
              <Box sx={{ height: "30px" }}>
                <img src={Logo} alt="logo" height="100%" width="100%" />
              </Box>
            </Box>
          </div>
        </div>
      </iframe>
    </div>
  );
}
